import React from 'react';
import {Link} from 'react-router-dom';
import styles from './Footer.module.css';
import siteConfig from '../../config/siteConfig';
import {useTranslation} from '../../hooks/useTranslation';
import LanguageToggle from '../ui/LanguageToggle';

function LinkedInIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
    </svg>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  const t = useTranslation();

  return (
    <footer className={styles.footer} role="contentinfo">
      <div className={`container ${styles.inner}`}>
        {/* Brand */}
        <div className={styles.brand}>
          <Link to="/" className={styles.logo} aria-label="Your Change Mentor — Home">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo-full.png`}
              alt="Your Change Mentor by Tamara Bonilla"
              className={styles.logoImg}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fb = e.currentTarget.parentElement.querySelector(`.${styles.logoFallback}`);
                if (fb) fb.style.display = 'flex';
              }}
            />
            <span className={styles.logoFallback}>
              <span className={styles.logoMark}>YCM</span>
              <span className={styles.logoName}>{siteConfig.site.name}</span>
            </span>
          </Link>
          <p className={styles.tagline}>{t.site.tagline}</p>
        </div>

        {/* Contact */}
        <div className={styles.contact}>
          <p className={styles.contactLabel}>{t.footer.contactLabel}</p>
          <a
            href={`mailto:${siteConfig.contact.email}`}
            className={styles.contactLink}
          >
            {siteConfig.contact.email}
          </a>
          <div className={styles.socials}>
            {siteConfig.contact.linkedin && (
              <a
                href={siteConfig.contact.linkedin}
                className={styles.socialLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect on LinkedIn"
              >
                <LinkedInIcon />
                LinkedIn
              </a>
            )}
            {siteConfig.contact.instagram && (
              <a
                href={siteConfig.contact.instagram}
                className={styles.socialLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow on Instagram"
              >
                <InstagramIcon />
                Instagram
              </a>
            )}
          </div>
        </div>

        {/* Legal */}
        <div className={styles.legal}>
          <nav aria-label="Legal links">
            <Link to="/privacy" className={styles.legalLink}>{t.footer.privacyPolicy}</Link>
            <Link to="/terms" className={styles.legalLink}>{t.footer.termsOfService}</Link>
          </nav>
          <p className={styles.copyright}>
            &copy; {year} {siteConfig.site.name}. {t.footer.copyright}
          </p>
          <div className={styles.langToggle}>
            <LanguageToggle />
          </div>
        </div>
      </div>
    </footer>
  );
}
